import { forwardRef, useImperativeHandle, useRef } from 'react';
import styled from '@emotion/styled';

import { tokens } from '@hotjar/hotsauce';
import { useWidthChanged } from '@hotjar/insights-common';
import type { DeviceType } from '@hotjar/webapp/domains/highlights/common/model';
import { numberFormat } from '@hotjar/insights-common/lib/number';
import { HEATMAP_DEVICE_TYPES } from '@hotjar/heatmaps';
import { useTranslatedClickEvent } from '@hotjar/webapp/features/Highlights/Heatmaps/use-translated-click-event';

import { viewBox } from '../Heatmaps/utils';
import { useImage } from '../SingleHighlight/use-image';

export const HIGHLIGHT_CARD_MAX_WIDTH = 240;

export const HIGHLIGHT_CARD_MAX_HEIGHT = 225;

const getSizingStylesForSize = (width: number, height: number) => {
  return width / height > HIGHLIGHT_CARD_MAX_WIDTH / HIGHLIGHT_CARD_MAX_HEIGHT
    ? { height: '100%', width: 'auto' }
    : { height: 'auto', width: '100%' };
};

const Wrapper = styled.div<{ width: number; height: number }>`
  display: block;
  ${({ width, height }) => {
    const sizes = getSizingStylesForSize(width, height);
    return `height: ${sizes.height}; width: ${sizes.width};`;
  }}
`;

const StyledSVG = styled.svg<{ scaleToHeight: boolean }>`
  ${({ scaleToHeight }) =>
    scaleToHeight
      ? `height: 100%;
width: auto;`
      : `height: auto;
width: 100%;`}
`;

export interface HeatmapImageProps {
  className?: string;
  selectedArea: {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  };
  url: string;
  strokeWidth?: number;
  clicksCount: number | null;
  clicksPercentage: number | null;
  deviceType: DeviceType;
}

export interface HeatmapImageRef {
  get: () => SVGSVGElement | null;
}

interface Box {
  w: number;
  h: number;
}

interface PositionedBox extends Box {
  x: number;
  y: number;
}

const StatsWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ClickStats = styled.div`
  display: flex;
  align-items: center;
  height: 32px;
  background-color: ${tokens.color.white};
  border-radius: ${tokens.radius.m};
  padding: 0 ${tokens.spacer.x1};
  ${tokens.text('subtitle2')};
`;

export const HeatmapHighlightPreview = forwardRef<HeatmapImageRef, HeatmapImageProps>(
  (
    { className, url, selectedArea, strokeWidth = 5, clicksCount, clicksPercentage, deviceType },
    ref,
  ) => {
    const selectedCopy = useTranslatedClickEvent({
      count: clicksCount ?? 0,
      isDesktop: deviceType === HEATMAP_DEVICE_TYPES.DESKTOP,
    });
    const svgRef = useRef<SVGSVGElement>(null);

    const divRef = useRef<HTMLDivElement>(null);
    const [cardWidth] = useWidthChanged(divRef);

    // proxy the component ref to the inner SVG element
    useImperativeHandle(ref, () => ({
      get: () => {
        return svgRef.current;
      },
    }));

    const { encodedImage, size } = useImage(url);
    const [height, width] = size;

    const cardSizeRatio = cardWidth / HIGHLIGHT_CARD_MAX_HEIGHT;
    const scaleToHeight = width && width / height > cardSizeRatio;

    if (!height && !width) {
      return null;
    }

    const highlightArea: PositionedBox = {
      x: selectedArea.x1,
      y: selectedArea.y1,
      w: selectedArea.x2 - selectedArea.x1,
      h: selectedArea.y2 - selectedArea.y1,
    };

    return (
      <Wrapper className={className} width={width} height={height} ref={divRef}>
        <StyledSVG
          scaleToHeight={!!scaleToHeight}
          ref={svgRef}
          viewBox={viewBox(
            {
              x: 0,
              y: 0,
              w: width,
              h: height,
            },
            { w: 0, h: 0 },
          )}
          height="100%"
          width="100%"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="xMinYMin meet"
        >
          <image href={encodedImage} width={width} height={height} />
          <rect
            x={highlightArea.x}
            y={highlightArea.y}
            width={highlightArea.w}
            height={highlightArea.h}
            fill="none"
            stroke={tokens.color.white}
            strokeWidth={strokeWidth}
            vectorEffect="non-scaling-stroke"
          />
        </StyledSVG>
        {clicksCount !== 0 && clicksPercentage !== 0 ? (
          <StatsWrapper>
            <ClickStats>{`${numberFormat(clicksCount ?? 0)} ${selectedCopy} (${numberFormat(
              clicksPercentage ?? 0,
            )}%)`}</ClickStats>
          </StatsWrapper>
        ) : null}
      </Wrapper>
    );
  },
);
